<template>
    <div class="map-page" :style="{ height: mapHeight }">
        <div v-if="error"
            class="alert alert-danger position-absolute top-0 start-50 translate-middle-x mt-3" style="z-index: 2;">
            {{ error }}
        </div>
        <div v-if="loading"
            class="position-absolute top-50 start-50 translate-middle text-body-tertiary" style="z-index: 1;">
            <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('common.loading') }}
        </div>
        <div v-if="!loading && !error && tasks.length === 0"
            class="position-absolute top-50 start-50 translate-middle fst-italic small text-body-tertiary"
            style="z-index: 1;">
            {{ $t('maps.noTasks') }}
        </div>
        <div v-if="!loading && !error && missingTasks.length > 0"
            class="map-missing position-absolute top-0 start-0 m-3" style="z-index: 3;">
            <button type="button"
                class="map-notice badge rounded-pill d-flex align-items-center bg-warning-subtle text-warning-emphasis border border-warning shadow-sm"
                @click="showMissing = !showMissing">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>{{ $t('maps.notOnMap', { count: missingTasks.length }) }}
                <i class="fa-solid ms-2" :class="showMissing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
            <div v-if="showMissing" class="card shadow border-warning mt-2 map-missing-panel">
                <div
                    class="card-header py-2 bg-warning-subtle text-warning-emphasis d-flex justify-content-between align-items-center">
                    <span class="fw-semibold small">{{ $t('maps.notOnMapTitle') }}</span>
                    <button type="button" class="btn-close" @click="showMissing = false"
                        :aria-label="$t('common.close')"></button>
                </div>
                <div class="list-group list-group-flush overflow-auto map-missing-list">
                    <router-link v-for="t in missingTasks" :key="t.id" :to="`/tasks/${t.id}`"
                        class="list-group-item list-group-item-action py-2">
                        <div class="small fw-semibold text-truncate">{{ t.title || t.project_name || ('#' + t.id) }}</div>
                        <div v-if="t.project_name" class="small text-body-tertiary text-truncate">{{ t.project_name }}</div>
                        <div v-if="t.address" class="small text-body-tertiary text-truncate">{{ t.address }}</div>
                    </router-link>
                </div>
            </div>
        </div>
        <div ref="mapEl" class="map-canvas"></div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { loadGoogleMaps } from '@/utils/googleMaps';

// Fallback view centred on Estonia when no markers can be fitted.
const ESTONIA_CENTER = { lat: 58.7, lng: 25.5 };

export default {
    name: 'MapsView',
    data() {
        return {
            tasks: [],
            loading: true,
            error: '',
            map: null,
            infoWindow: null,
            markers: [],
            badges: [],
            stackBadges: {},
            oms: null,
            // Assigned tasks that have no coordinates, so they can't be plotted.
            missingTasks: [],
            showMissing: false,
            // Fills the viewport below the navbar; refined once mounted so the
            // exact navbar height is known.
            mapHeight: 'calc(100vh - 56px)',
        };
    },
    async mounted() {
        this.updateMapHeight();
        window.addEventListener('resize', this.updateMapHeight);
        try {
            const [google] = await Promise.all([loadGoogleMaps(), this.fetchTasks()]);
            // OMS reads `google.maps` at module-evaluation time, so it can only be
            // imported after the Maps API is on the page.
            const { default: OverlappingMarkerSpiderfier } = await import('@/utils/oms.js');
            this.initMap(google);
            this.drawMarkers(google, OverlappingMarkerSpiderfier);
        } catch (err) {
            console.error('Maps init failed:', err);
            this.error = this.$t('maps.loadError');
        } finally {
            this.loading = false;
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateMapHeight);
        if (this.oms) this.oms.clearMarkers();
        this.markers.forEach(m => m.setMap(null));
        this.badges.forEach(b => b.setMap(null));
        this.markers = [];
        this.badges = [];
    },
    methods: {
        updateMapHeight() {
            const nav = document.querySelector('.navbar');
            const navH = nav ? nav.offsetHeight : 56;
            this.mapHeight = `${window.innerHeight - navH}px`;
            if (this.map) window.google.maps.event.trigger(this.map, 'resize');
        },
        async fetchTasks() {
            const response = await axios.get('tasks/map');
            const all = response.data?.data || [];
            // Guard against null/'' coords: Number(null) is 0 (not NaN), which would
            // otherwise slip through as a valid (0, 0) marker off the African coast.
            const hasCoords = (t) => {
                if (t.lat == null || t.lat === '' || t.lng == null || t.lng === '') return false;
                return Number.isFinite(Number(t.lat)) && Number.isFinite(Number(t.lng));
            };
            // Only coordinate tasks go on the map; the rest are counted for the
            // notice. No geocoding, so the coordinate-less ones cost nothing.
            this.tasks = all.filter(hasCoords);
            this.missingTasks = all.filter(t => !hasCoords(t));
        },
        initMap(google) {
            this.map = new google.maps.Map(this.$refs.mapEl, {
                center: ESTONIA_CENTER,
                zoom: 7,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                // Hide default points-of-interest (businesses etc.) so only our
                // task markers stand out.
                styles: [
                    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                    { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
                ],
            });
            this.infoWindow = new google.maps.InfoWindow();
        },
        // Shape encodes priority (matching the main web app): 1 maintenance =
        // circle, 2 fault/order = triangle, 3 installation = square. Colour and
        // border come from the API.
        markerIcon(google, task) {
            const base = {
                fillColor: task.color || '#0058cc',
                fillOpacity: 1,
                strokeColor: task.border || '#fff',
                strokeWeight: 2,
            };
            switch (Number(task.priority)) {
                case 2:
                    return { ...base, path: 'M 0,-9 L 8,7 L -8,7 Z', scale: 1 };
                case 3:
                    return { ...base, path: 'M -7,-7 L 7,-7 L 7,7 L -7,7 Z', scale: 1 };
                default:
                    return { ...base, path: google.maps.SymbolPath.CIRCLE, scale: 8 };
            }
        },
        drawMarkers(google, OverlappingMarkerSpiderfier) {
            const bounds = new google.maps.LatLngBounds();

            // Fans out markers that share coordinates so each is individually
            // clickable. Nudging is off so stacked markers stay perfectly under
            // their count badge until the stack is fanned out.
            this.oms = new OverlappingMarkerSpiderfier(this.map, {
                markersWontHide: true,
                keepSpiderfied: true,
                nudgeStackedMarkers: false,
                circleSpiralSwitchover: 5,
            });
            this.oms.addListener('click', (marker) => this.openInfo(marker.task, marker));

            // Group tasks by exact coordinate; identical-location stacks get a badge.
            const groups = new Map();
            this.tasks.forEach(task => {
                const lat = Number(task.lat);
                const lng = Number(task.lng);
                const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
                const position = { lat, lng };
                const marker = new google.maps.Marker({
                    position,
                    map: this.map,
                    title: task.title || task.project_name || `#${task.id}`,
                    icon: this.markerIcon(google, task),
                });

                marker.task = task;
                marker.stackKey = key;
                // OMS manages the marker's click handling.
                this.oms.addMarker(marker);
                this.markers.push(marker);
                bounds.extend(position);

                if (!groups.has(key)) groups.set(key, { position, members: [] });
                groups.get(key).members.push(marker);
            });

            // Purple count badge drawn on top of each multi-task stack. Clicking it
            // fans the stack; it hides while spiderfied and reappears on collapse.
            groups.forEach((group, key) => {
                if (group.members.length < 2) return;
                const badge = new google.maps.Marker({
                    position: group.position,
                    map: this.map,
                    zIndex: 1000000,
                    title: '',
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 13,
                        fillColor: '#6f42c1',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 2,
                    },
                    label: { text: String(group.members.length), color: '#fff', fontSize: '12px', fontWeight: '700' },
                });
                badge.addListener('click', () => window.google.maps.event.trigger(group.members[0], 'click'));
                this.stackBadges[key] = badge;
                this.badges.push(badge);
            });

            this.oms.addListener('spiderfy', (spiderfied) => {
                const badge = this.stackBadges[spiderfied[0]?.stackKey];
                if (badge) badge.setVisible(false);
            });
            this.oms.addListener('unspiderfy', (unspiderfied) => {
                const badge = this.stackBadges[unspiderfied[0]?.stackKey];
                if (badge) badge.setVisible(true);
            });

            if (this.markers.length === 1) {
                this.map.setCenter(bounds.getCenter());
                this.map.setZoom(14);
            } else if (this.markers.length > 1) {
                this.map.fitBounds(bounds);
            }
        },
        openInfo(task, marker) {
            const esc = s => String(s ?? '').replace(/[&<>"']/g, c => (
                { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
            ));

            const rows = [];
            if (task.project_name) rows.push(esc(task.project_name));
            if (task.realestate_name && task.realestate_name !== task.project_name) rows.push(esc(task.realestate_name));
            if (task.address) rows.push(`<span class="text-body-tertiary">${esc(task.address)}</span>`);

            const dates = [];
            if (task.estimated_date) dates.push(`${esc(this.$t('task.planned'))}: ${esc(task.estimated_date)}`);
            if (task.due_date) dates.push(`${esc(this.$t('task.dueDate'))}: ${esc(task.due_date)}`);

            const title = task.title || task.project_name || `#${task.id}`;
            const btnId = `map-open-task-${task.id}`;

            this.infoWindow.setContent(`
                <div class="small" style="max-width: 240px;">
                    <div class="fw-bolder mb-1">${esc(title)}</div>
                    ${rows.map(r => `<div>${r}</div>`).join('')}
                    ${dates.length ? `<div class="mt-1">${dates.map(d => `<div>${d}</div>`).join('')}</div>` : ''}
                    <button id="${btnId}" type="button" class="btn btn-sm btn-primary mt-2">
                        ${esc(this.$t('maps.openTask'))}
                    </button>
                </div>
            `);

            window.google.maps.event.addListenerOnce(this.infoWindow, 'domready', () => {
                const btn = document.getElementById(btnId);
                if (btn) btn.addEventListener('click', () => this.$router.push(`/tasks/${task.id}`));
            });

            this.infoWindow.open({ map: this.map, anchor: marker });
        },
    },
};
</script>

<style scoped>
.map-page {
    position: relative;
    width: 100%;
}

.map-canvas {
    width: 100%;
    height: 100%;
}

.map-notice {
    cursor: pointer;
    font-size: 0.8rem;
}

.map-missing-panel {
    width: min(320px, 80vw);
}

.map-missing-list {
    max-height: 45vh;
}
</style>
